/*
 * Copyright (c) 2026 CalAmp Corp. All Rights Reserved
 */
import React, {forwardRef, useMemo} from 'react';
import {ColDef} from 'ag-grid-community';
import {translate} from '@ion/core/src/locale/locale';
import {OfflineCanRequestResult} from '@ion/core/src/types/services';
import {resolveDefinitions} from '@ion/dls/src/grid/ColumnResolver';
import IonGrid, {IonGridAPI, RowModelTypes} from '@ion/dls/src/grid/IonGrid';
import {useColumnChooser} from '@ion/dls/src/grid/useColumnChooser';
import {useColumnState} from '@ion/dls/src/grid/useColumnState';
import Render from '@ion/dls/src/render/Render';
import CanRequestSummaryAccordion from './CanRequestSummaryAccordion';
import {createCanRequestDetailsRows} from './CanRequestUtils';

export interface CanRequestDetailsGridProps {
  requestDetails: OfflineCanRequestResult;
  loading?: boolean;
}

/**
 * Renders response-level CAN frame details for a selected CAN request.
 *
 * The component composes a request summary accordion and a details grid that
 * only displays rows mapped from `canResponses`.
 *
 * ```typescript
 * import CanRequestDetailsGrid from
 *   '@ion/applications/src/pages/vehicle/manager/CanRequestDetailsGrid';
 * ...
 *
 * <CanRequestDetailsGrid requestDetails={requestDetails}
 *   loading={isLoadingDetails} />
 * ```
 *
 * @param   requestDetails The selected CAN request details payload.
 * @param   loading Determines whether the grid loading state is displayed.
 * @returns The request summary and CAN response details grid.
 */
const CanRequestDetailsGrid = forwardRef<IonGridAPI, CanRequestDetailsGridProps>(({
  requestDetails,
  loading
}, ref) => {
  const columnState = null;

  const {definitions, defaultDefinitions} = useColumnState({
    columnDefinitions : getColumnDefinitions(),
    columnState
  });

  const {columnDefinitions} = useColumnChooser(definitions);

  const detailRows = useMemo(() => createCanRequestDetailsRows(requestDetails), [requestDetails]);

  return (
    <>
      <Render.If condition={!loading}>
        <CanRequestSummaryAccordion requestDetails={requestDetails} />
      </Render.If>
      <IonGrid ref={ref}
        isLoading={loading}
        onColumnChooserClose={function() { }}
        rowData={detailRows}
        rowModelType={RowModelTypes.ClientSide}
        defaultDefinitions={defaultDefinitions}
        checkboxesEnabled={false}
        columnDefinitions={columnDefinitions || []}
        isDialogControl
        omitHeader />
    </>
  );
});

const defaultColumn = (headerName: string, field: string): ColDef => ({
  headerName,
  field,
  filter       : true,
  filterParams : {
    buttons      : ['reset', 'clear', 'apply'],
    closeOnApply : true
  },
  resizable : true,
  hide      : false
});

const getCommonColumnDefinitions = (): ColDef[] => {
  const {
    identifier = '',
    channel = '',
    dlc = '',
    rxId01 = '',
    mask = '',
    data = ''
  } = (translate('vehicle-manager:vehicleHistoryGrid.canRequestDetailsGrid', {returnObjects: true}) || {});

  return [
    defaultColumn(identifier, 'identifier'),
    defaultColumn(channel, 'channel'),
    defaultColumn(dlc, 'dlc'),
    defaultColumn(rxId01, 'rxId01'),
    defaultColumn(mask, 'mask'),
    {
      ...defaultColumn(data, 'data'),
      minWidth : 250
    }
  ];
};

const getColumnDefinitions = () => {
  const columns = resolveDefinitions(getCommonColumnDefinitions());
  return ({
    desktop : columns,
    mobile  : columns
  });
};

export default CanRequestDetailsGrid;
